'use client'

import React, { Fragment, useState } from 'react'
import type { SeoBlock } from '@/lib/apis/sanity'
import { PortableText, PortableTextReactComponents } from '@portabletext/react'
import { Container } from '@/shared/components/Container'
import clsx from 'clsx'
import Link from 'next/link'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { motion } from 'framer-motion'

interface Props {
  data: SeoBlock
}

const SeoBlock: React.FC<Props> = ({ data }) => {
  const [selectedBlock, setSelectedBlock] = useState(0)

  return (
    <div className="my-20">
      <Container className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        <div className="col-span-1 flex flex-col gap-y-2">
          {data.paragraphs.map((block, idx) => (
            <Fragment key={idx}>
              <button
                className={clsx(
                  'group flex w-full items-center justify-between gap-x-2.5 rounded-2xl py-3 pl-4 pr-2 font-psans text-rich-black transition-all hover:bg-gray-100 sm:rounded-3xl sm:pl-6 sm:pr-3',
                  selectedBlock === idx && 'bg-clinical-white',
                )}
                onClick={() => setSelectedBlock(idx)}
                aria-pressed={idx === selectedBlock}
                aria-label={`Select block ${idx + 1}`}
                role="button"
              >
                <h2 className="text-left text-base font-medium sm:text-lg">
                  {block.title}
                </h2>
                <div
                  className={clsx(
                    'hidden h-8 w-8 shrink-0 items-center justify-center rounded-full sm:flex sm:h-10 sm:w-10',
                    selectedBlock === idx && 'bg-blue-600 text-clinical-white',
                  )}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
                <motion.div
                  className={clsx(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all sm:hidden sm:h-10 sm:w-10',
                    selectedBlock === idx && 'bg-blue-600 text-clinical-white',
                  )}
                  initial={{ rotate: 0 }}
                  animate={{ rotate: selectedBlock === idx ? 90 : 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </motion.div>
              </button>
              <div className="block overflow-hidden sm:hidden">
                <motion.div
                  className={clsx('rounded-3xl bg-clinical-white')}
                  initial={{ opacity: 0, height: 0 }}
                  animate={
                    selectedBlock === idx
                      ? { opacity: 1, height: 'auto' }
                      : { opacity: 0, height: 0 }
                  }
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="px-6 py-3 font-psans text-rich-black">
                    <PortableText
                      value={block.content}
                      components={Components}
                    />
                  </div>
                </motion.div>
              </div>
            </Fragment>
          ))}
        </div>
        <div className="col-span-1 hidden min-h-full items-center rounded-4xl bg-clinical-white px-10 py-8 sm:flex">
          <motion.div
            className="font-psans text-rich-black"
            key={selectedBlock}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <PortableText
              value={data.paragraphs[selectedBlock].content}
              components={Components}
            />
          </motion.div>
        </div>
      </Container>
    </div>
  )
}

const Components: Partial<PortableTextReactComponents> = {
  list: {
    bullet: ({ children }) => (
      <ul className="my-2.5 max-w-3xl list-disc space-y-2 sm:space-y-4">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="my-2.5 list-none space-y-4">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="ml-4 text-gray-700 sm:ml-8">{children}</li>
    ),
    number: ({ children, index }) => (
      <li className="flex items-center gap-x-3.5 text-gray-700 sm:gap-x-6">
        <div className="flex h-8 w-8 max-w-none shrink-0 items-center justify-center rounded-full border border-blue-600 text-base font-bold text-blue-600 sm:h-10 sm:w-10">
          {index + 1}
        </div>
        <p>{children}</p>
      </li>
    ),
  },

  block: {
    h1: ({ children }) => (
      <h1 className="my-4 font-moisette text-3xl font-medium text-gray-800">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="my-4 font-moisette text-3xl font-medium text-gray-800">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="my-4 font-moisette text-2xl font-medium text-gray-800">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="my-4 font-moisette text-xl font-medium text-gray-800">
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="text mx-auto my-3 w-[80%] text-center font-semibold italic text-gray-500">
        {'"'}
        {children}
        {'"'}
      </blockquote>
    ),
    normal: ({ children }) => (
      <p className="text-lg font-light text-gray-700">{children}</p>
    ),
  },

  marks: {
    link: ({ value, children }) => {
      return (
        <Link
          className="cursor-pointer text-blue-600 underline underline-offset-2 transition-all hover:text-blue-500"
          href={value?.href}
        >
          {children}
        </Link>
      )
    },
  },

  hardBreak: false,
}

export default SeoBlock
